import { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Logo from "@/components/Logo";
import ReviewsSection from "@/components/ReviewsSection";
import { Badge } from "@/components/ui/badge";
import { useProducts } from "@/hooks/useProducts";
import { photoUrl } from "@/utils/photoUrl";
import ProductGallery from "@/components/ProductGallery";
import { useCart, useCartCount } from "@/hooks/useCart";
import { useReviewSummary } from "@/hooks/useReviewSummary";
import { toast } from "sonner";
import {
  ArrowLeft, ShoppingCart, ShieldCheck, FlaskConical,
  Snowflake, BadgeCheck, ShoppingBag, Plus, Minus, AlertTriangle, Star,
} from "lucide-react";

const HIGHLIGHTS = [
  { icon: FlaskConical, label: "HPLC tested · ≥ 99% purity" },
  { icon: ShieldCheck, label: "Third-party lab verified" },
  { icon: Snowflake, label: "Cold chain shipping" },
  { icon: BadgeCheck, label: "Anti-counterfeit batch code" },
];

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const products = useProducts();
  const product = useMemo(() => products.find((p) => p.id === id), [products, id]);

  const [qty, setQty] = useState(1);
  const { addItem } = useCart();
  const cartCount = useCartCount();

  const handleAdd = () => {
    if (!product) return;
    addItem(product, qty);
    toast.success(`${qty} × ${product.name} added to cart`);
  };

  if (!product) {
    return (
      <div className="min-h-screen bg-[#020409] text-white flex flex-col items-center justify-center px-6">
        <p className="text-zinc-400 mb-4">Product not found.</p>
        <button
          onClick={() => navigate("/products")}
          data-testid="back-to-catalog"
          className="text-[#39FF14] hover:underline text-sm uppercase tracking-wider"
        >
          ← Back to catalog
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020409] text-white">
      {/* Top strip */}
      <div className="border-b border-white/5 bg-black/60 backdrop-blur-md text-[11px] uppercase tracking-[0.2em] font-medium">
        <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            data-testid="product-back-btn"
            className="flex items-center gap-2 text-zinc-500 hover:text-white transition"
          >
            <ArrowLeft className="w-3 h-3 text-[#39FF14]" /> Back
          </button>
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#020409]/85 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="product-logo">
            <Logo />
          </button>
          <button
            onClick={() => navigate("/")}
            className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-[#39FF14] transition"
            data-testid="product-all-link"
          >
            ← Home
          </button>
          <button
            onClick={() => navigate("/cart")}
            data-testid="product-cart-link"
            className="relative inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-[#39FF14] transition"
          >
            <ShoppingBag className="w-4 h-4" /> Cart
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-3 w-4 h-4 rounded-full bg-[#39FF14] text-black text-[9px] font-bold flex items-center justify-center">{cartCount}</span>
            )}
          </button>
        </div>
      </header>

      {/* DETAIL */}
      <main className="max-w-7xl mx-auto px-6 py-12 lg:py-16 grid lg:grid-cols-2 gap-12">
        {/* Image + gallery */}
        <div className="relative">
          <ProductGallery
            alt={product.name}
            images={[
              photoUrl(product.photo),
              ...(Array.isArray(product.gallery)
                ? product.gallery.map((u) =>
                    u.startsWith("data:") || u.startsWith("http")
                      ? u
                      : `${process.env.REACT_APP_BACKEND_URL}${u}`,
                  )
                : []),
            ]}
          />

          {/* Quantity + Add to Cart — directly under the photo */}
          <div className="grid sm:grid-cols-[160px_1fr] gap-3 mt-4">
            <div className="flex items-center gap-2 bg-[#0A0F1A] border border-white/10 rounded-md h-[52px] px-2 justify-between">
              <button
                type="button"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                data-testid="product-qty-dec"
                className="w-9 h-9 rounded-md hover:bg-white/10 flex items-center justify-center text-white"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span data-testid="product-qty-value" className="font-bold text-white tabular-nums text-lg">{qty}</span>
              <button
                type="button"
                onClick={() => setQty((q) => q + 1)}
                data-testid="product-qty-inc"
                className="w-9 h-9 rounded-md hover:bg-white/10 flex items-center justify-center text-white"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={handleAdd}
              data-testid="product-add-to-cart-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#39FF14] hover:bg-[#2adc0c] text-black font-bold uppercase tracking-wider text-sm h-[52px] rounded-md transition shadow-[0_0_15px_rgba(57,255,20,0.4)] hover:shadow-[0_0_25px_rgba(57,255,20,0.6)]"
            >
              <ShoppingCart className="w-4 h-4" /> Add to Cart
            </button>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3" data-testid="product-highlights">
            {HIGHLIGHTS.map((h, i) => (
              <div key={i} className="flex items-center gap-2.5 bg-[#0A0F1A] border border-white/5 rounded-lg px-3 py-2.5">
                <div className="w-7 h-7 rounded-full bg-[#39FF14]/10 border border-[#39FF14]/30 flex items-center justify-center shrink-0">
                  <h.icon className="w-3.5 h-3.5 text-[#39FF14]" />
                </div>
                <span className="text-[11px] font-medium text-zinc-300 leading-tight">{h.label}</span>
              </div>
            ))}
          </div>

          {/* (moved Add to Cart above) */}
        </div>

        {/* Info */}
        <div className="flex flex-col">
          <p className="text-[11px] uppercase tracking-[0.3em] text-blue-400 font-bold mb-3" data-testid="product-category">
            {product.category}
          </p>
          <h1
            data-testid="product-name"
            className="font-headline text-3xl md:text-5xl font-black tracking-tighter mb-3 leading-[1.05]"
          >
            {product.name}
          </h1>
          <p className="text-zinc-400 text-sm uppercase tracking-[0.15em] mb-3" data-testid="product-strength">
            {product.strength} · {product.volume}
          </p>

          {/* Rating row */}
          <ProductRatingRow productId={product.id} />

          <div className="flex flex-wrap gap-2 mb-6">
            {product.tags.map((t) => (
              <Badge
                key={t}
                className="bg-black border border-[#39FF14]/25 text-[#39FF14] text-[10px] uppercase tracking-[0.15em] font-bold py-1 px-2.5"
              >
                {t}
              </Badge>
            ))}
          </div>

          <div className="flex items-baseline gap-3 mb-7">
            <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 font-bold">Price</div>
            <div
              data-testid="product-price"
              className="font-headline font-black text-4xl text-[#39FF14] glow-green"
            >
              €{product.price}
            </div>
            <div className="text-xs text-zinc-500">per unit · research grade</div>
          </div>

          <div className="border border-white/5 bg-[#0A0F1A] rounded-xl p-5 mb-5">
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#39FF14] font-bold mb-2">Description</p>
            <p data-testid="product-description" className="text-zinc-300 leading-relaxed text-sm mb-3">
              {product.description}
            </p>
            {product.longDescription && (
              <p data-testid="product-long-description" className="text-zinc-400 leading-relaxed text-sm">
                {product.longDescription}
              </p>
            )}
          </div>

          <div
            data-testid="product-research-disclaimer"
            className="flex items-start gap-3 rounded-xl border border-red-500/40 bg-red-500/[0.06] px-4 py-3 mb-6"
          >
            <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
            <div className="text-xs leading-relaxed">
              <span className="font-bold uppercase tracking-wider text-red-400">Research Use Only</span>
              <span className="text-zinc-300"> — Not for human consumption. For in-vitro laboratory research only.</span>
            </div>
          </div>
        </div>
      </main>

      {/* REVIEWS */}
      <div className="max-w-7xl mx-auto px-6 pb-20">
        <ReviewsSection productId={product.id} />
      </div>
    </div>
  );
}

function ProductRatingRow({ productId }) {
  const summary = useReviewSummary(productId);
  const count = summary?.count || 0;
  const avg = summary?.avg || 0;
  const scrollToReviews = () => {
    const el = document.getElementById("reviews");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <button
      type="button"
      onClick={scrollToReviews}
      data-testid="product-rating-row"
      className="inline-flex items-center gap-2 mb-5 group"
    >
      <div className="inline-flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star
            key={i}
            className={`w-4 h-4 ${
              i <= Math.round(avg) ? "text-[#39FF14] fill-[#39FF14]" : "text-zinc-700"
            }`}
            style={i <= Math.round(avg) ? { filter: "drop-shadow(0 0 4px rgba(57,255,20,0.55))" } : undefined}
          />
        ))}
      </div>
      <span className="text-xs text-zinc-400 group-hover:text-[#39FF14] transition">
        {count > 0 ? (
          <>
            <span className="text-[#39FF14] font-bold">{avg.toFixed(1)}</span>{" "}
            <span className="text-zinc-500">·</span> {count} review{count === 1 ? "" : "s"}
          </>
        ) : (
          "No reviews yet"
        )}
      </span>
    </button>
  );
}
